/**
 * Utility constants and functions for generating entity-specific QR code values.
 * QR code values include an entity prefix to prevent conflicts when encrypted IDs
 * share the same value across different entity types (e.g. Store vs Partner).
 */

export const STORE_QR_PREFIX = 'store-';
export const PARTNER_QR_PREFIX = 'partner-';

export type QrEntityType = 'store' | 'partner';

/**
 * Generates the QR Code value for a Store entity.
 * Format: store-{encrypted_store_id}
 *
 * @param encryptedStoreId - The encrypted store ID
 * @returns The formatted QR Code value with 'store-' prefix, or empty string if no ID provided.
 */
export const getStoreQrValue = (encryptedStoreId?: string | null): string => {
  if (!encryptedStoreId) return '';
  if (encryptedStoreId.startsWith(STORE_QR_PREFIX)) return encryptedStoreId;
  return `${STORE_QR_PREFIX}${encryptedStoreId}`;
};

/**
 * Generates the QR Code value for a Partner entity.
 * Format: partner-{encrypted_partner_id}
 *
 * @param encryptedPartnerId - The encrypted partner ID
 * @returns The formatted QR Code value with 'partner-' prefix, or empty string if no ID provided.
 */
export const getPartnerQrValue = (encryptedPartnerId?: string | null): string => {
  if (!encryptedPartnerId) return '';
  if (encryptedPartnerId.startsWith(PARTNER_QR_PREFIX)) return encryptedPartnerId;
  return `${PARTNER_QR_PREFIX}${encryptedPartnerId}`;
};

/**
 * Resolves QR code value by entity type.
 */
export const getEntityQrValue = (
  entityType: QrEntityType,
  encryptedId?: string | null
): string => {
  if (entityType === 'store') return getStoreQrValue(encryptedId);
  if (entityType === 'partner') return getPartnerQrValue(encryptedId);
  return encryptedId || '';
};

// Aliases for explicit formatting nomenclature
export const formatStoreQrValue = getStoreQrValue;
export const formatPartnerQrValue = getPartnerQrValue;
